import React, { useEffect, useMemo, useState } from 'react';

import { Dayjs } from 'dayjs';
import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import dayjs from '~/timeInit';

import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { indigo, teal } from '@mui/material/colors';
import { DateTimePicker } from '@mui/x-date-pickers-pro';

import PageHeader from '~/components/Common/Elements/PageHeader';

import { allRegions } from '~/consts/allRegions';

import { useCreateRegionChannelSetting } from '~/hooks/apis/promo/useCreateRegionChannelSetting';
import { useDeleteRegionChannelSetting } from '~/hooks/apis/promo/useDeleteRegionChannelSetting';
import { useListRegionChannelSettings } from '~/hooks/apis/promo/useListRegionChannelSettings';
import { useUpdateRegionChannelSetting } from '~/hooks/apis/promo/useUpdateRegionChannelSetting';

import type {
  RegionChannelSetting,
  RegionChannelSettingPayload,
  RegionChannelSettingType,
} from '~/services/PromoService';

import RegionRuleResolveTab from './RegionRuleResolveTab';

type SettingColumn = { key: string; label: string };
type FormField = { key: string; label: string; helper?: string };

/**
 * Registry of setting types. Each type declares how its rows render (columns) and how its
 * create/edit form is built + mapped to a payload. Adding a new setting type is a new entry
 * here (plus the matching svc-promo `setting_type`) — the page shell is type-agnostic.
 */
type SettingTypeConfig = {
  type: RegionChannelSettingType;
  label: string;
  columns: Array<SettingColumn>;
  getCell: (setting: RegionChannelSetting, key: string) => string | null;
  formFields: Array<FormField>;
  formChannelHelper?: string;
  toPayload: (form: Record<string, string>, regionCode: string | null) => RegionChannelSettingPayload;
  initialForm: (setting?: RegionChannelSetting) => Record<string, string>;
};

// Shared by every setting type that scopes on UTM columns. Blank and '*' mean different things
// on the svc-promo side, so the helper text spells it out for operators.
const UTM_HELPER =
  "Blank = applies to all traffic. '*' = the UTM must be present (any value). 'cpp*' matches by prefix.";

const UTM_COLUMNS = [
  { key: 'source', label: 'Source' },
  { key: 'medium', label: 'Medium' },
  { key: 'campaign', label: 'Campaign' },
  { key: 'term', label: 'Term' },
  { key: 'content', label: 'Content' },
];
const UTM_FORM_FIELDS: Array<FormField> = [
  { key: 'source', label: 'utm_source' },
  { key: 'medium', label: 'utm_medium' },
  { key: 'campaign', label: 'utm_campaign' },
  { key: 'term', label: 'utm_term' },
  { key: 'content', label: 'utm_content' },
];
const utmCell = (setting: RegionChannelSetting, key: string) =>
  setting[key as 'source' | 'medium' | 'campaign' | 'term' | 'content'] ?? null;
const utmToPayload =
  (type: RegionChannelSettingType) =>
  (form: Record<string, string>, regionCode: string | null): RegionChannelSettingPayload => ({
    setting_type: type,
    region_code: regionCode ?? undefined,
    source: form.source?.trim() || undefined,
    medium: form.medium?.trim() || undefined,
    campaign: form.campaign?.trim() || undefined,
    term: form.term?.trim() || undefined,
    content: form.content?.trim() || undefined,
  });
const utmInitialForm = (setting?: RegionChannelSetting) => ({
  source: setting?.source ?? '',
  medium: setting?.medium ?? '',
  campaign: setting?.campaign ?? '',
  term: setting?.term ?? '',
  content: setting?.content ?? '',
});

const SETTING_TYPES: Array<SettingTypeConfig> = [
  {
    type: 'free_preview_auto_enrol',
    label: 'Free Preview auto-enrolment (new users)',
    columns: UTM_COLUMNS,
    getCell: utmCell,
    formFields: UTM_FORM_FIELDS,
    formChannelHelper: UTM_HELPER,
    toPayload: utmToPayload('free_preview_auto_enrol'),
    initialForm: utmInitialForm,
  },
  {
    // Same rule shape, different audience: this one enrols customers who already have an account.
    // The two resolve independently, so a campaign aimed at everyone needs a rule in both.
    type: 'free_preview_auto_enrol_for_existing_users',
    label: 'Free Preview auto-enrolment (existing users)',
    columns: UTM_COLUMNS,
    getCell: utmCell,
    formFields: UTM_FORM_FIELDS,
    formChannelHelper: UTM_HELPER,
    toPayload: utmToPayload('free_preview_auto_enrol_for_existing_users'),
    initialForm: utmInitialForm,
  },
  {
    type: 'offer_region_filter_bypass',
    label: 'Offer Region Bypass (MLOS)',
    columns: UTM_COLUMNS,
    getCell: utmCell,
    formFields: UTM_FORM_FIELDS,
    formChannelHelper: UTM_HELPER,
    toPayload: utmToPayload('offer_region_filter_bypass'),
    initialForm: utmInitialForm,
  },
];

/**
 * Local type scale. The app theme sets every heading to `normal` weight and leaves only 4px between
 * h3 and h6, so on a page this dense hierarchy has to come from weight, letter-spacing and colour.
 * Section names read as eyebrows (they are context) and the region name is the loudest thing on the
 * card (it is what you are looking for when you scroll).
 */
const TYPE_SCALE = {
  section: {
    display: 'block',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.09em',
    textTransform: 'uppercase',
    color: 'text.secondary',
  },
  regionName: { fontSize: 17, fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.3 },
  typeLabel: { fontSize: 13, fontWeight: 600, letterSpacing: '0.01em' },
  subLabel: {
    display: 'block',
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'text.disabled',
  },
  columnHead: {
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: '0.07em',
    textTransform: 'uppercase',
    color: 'text.secondary',
    whiteSpace: 'nowrap',
  },
} as const;

// Scope is carried by a rail colour that survives scrolling, so wherever you land you can tell which
// level you are reading. Both hues come from MUI's own palette, the same source as the app theme.
const SCOPE_RAIL = { global: indigo[400], region: teal[400] } as const;

const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

/**
 * Every table on the page shares one geometry so the UTM columns line up down the whole card —
 * inherited and region-specific sections have different trailing columns (a toggle vs status +
 * actions), and left to size themselves the channel columns would land in different places in
 * each section. `fixed` makes the header widths authoritative.
 */
const FIXED_TABLE = { tableLayout: 'fixed' as const, minWidth: 820 };
// The trailing columns claim 32% in both sections, so the channel columns share what is left
// rather than a fixed each — otherwise adding one (utm_content) pushes the total past 100%.
const CHANNEL_COLS_TOTAL_PCT = 68;
const utmColWidth = (columnCount: number) => `${CHANNEL_COLS_TOTAL_PCT / columnCount}%`;

/**
 * A stored scoping value, rendered so its precedence is legible from the value itself: an exact
 * value is plain, a glob has its `*` tinted so it can never be read as a literal, and an empty
 * column is a muted dash.
 */
function PatternValue({ value }: { value?: string | null }) {
  if (!value) {
    // A dash rather than words: in a dense table of patterns, a muted dash disappears into the
    // background, which is what "this column constrains nothing" should look like.
    return (
      <Tooltip title="No constraint — applies to all traffic, including visitors without this UTM">
        <Typography component="span" variant="body2" sx={{ fontFamily: MONO, color: 'text.disabled' }}>
          —
        </Typography>
      </Tooltip>
    );
  }
  // A bare '*' needs no special case — it falls out of the glob rendering below as a lone tinted
  // asterisk, so the wildcard always looks the same whether it stands alone or sits in 'cpp*'.
  const parts = value.split('*');
  return (
    <Tooltip title={value === '*' ? 'The UTM must be present, with any value' : ''}>
      <Typography component="span" variant="body2" sx={{ fontFamily: MONO }}>
        {parts.map((part, i) => (
          <React.Fragment key={i}>
            {part}
            {i < parts.length - 1 && (
              <Box component="span" sx={{ color: 'primary.main', fontWeight: 700 }}>
                *
              </Box>
            )}
          </React.Fragment>
        ))}
      </Typography>
    </Tooltip>
  );
}

/** Surfaces a scheduling window that would otherwise only be visible inside the edit dialog. */
function WindowNote({ setting }: { setting: RegionChannelSetting }) {
  const now = Date.now();
  const start = setting.start_datetime ? new Date(setting.start_datetime) : null;
  const end = setting.end_datetime ? new Date(setting.end_datetime) : null;
  if (!start && !end) {
    return null;
  }
  const day = (d: Date) => d.toISOString().slice(0, 10);
  const pending = start && start.getTime() > now;
  const over = end && end.getTime() < now;
  return (
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5 }}>
      {(pending || over) && (
        <Chip
          size="small"
          color="warning"
          variant="outlined"
          label={pending ? 'Not started' : 'Ended'}
          sx={{ height: 18, fontSize: 10 }}
        />
      )}
      {/* An unbounded end is '∞' rather than an ellipsis — an ellipsis reads as "truncated", which
          is the opposite of what an open-ended window means. */}
      <Typography variant="caption" sx={{ fontFamily: MONO, fontSize: 10.5, color: 'text.disabled' }}>
        <Tooltip title={start ? '' : 'No start bound — active as soon as it is enabled'}>
          <Box component="span">{start ? day(start) : '∞'}</Box>
        </Tooltip>
        {' → '}
        <Tooltip title={end ? '' : 'No end bound — runs until disabled'}>
          <Box component="span">{end ? day(end) : '∞'}</Box>
        </Tooltip>
      </Typography>
    </Stack>
  );
}

const configFor = (type: string) => SETTING_TYPES.find((t) => t.type === type);
const regionName = (code?: string | null) => allRegions.find((r) => r.code === code)?.name ?? code ?? 'Global';
const channelKey = (s: RegionChannelSetting) =>
  `${s.setting_type}|${s.source ?? ''}|${s.medium ?? ''}|${s.campaign ?? ''}|${s.term ?? ''}|${s.content ?? ''}`;

// The datetime bounds are entered and displayed as UTC, and held in form state as the ISO string
// that goes to svc-promo. An absent bound is `null` rather than a blank string, so "cleared" and
// "half-entered" cannot be confused.
const isoToPicker = (iso?: string | null): Dayjs | null => {
  if (!iso) {
    return null;
  }
  const d = dayjs.utc(iso);
  return d.isValid() ? d : null;
};
const pickerToIso = (value: Dayjs | null): string | undefined =>
  value?.isValid() ? value.utc().toISOString() : undefined;

const settingToPayload = (s: RegionChannelSetting): RegionChannelSettingPayload => ({
  setting_type: s.setting_type,
  region_code: s.region_code ?? undefined,
  source: s.source ?? undefined,
  medium: s.medium ?? undefined,
  campaign: s.campaign ?? undefined,
  term: s.term ?? undefined,
  content: s.content ?? undefined,
  enabled: s.enabled,
  start_datetime: s.start_datetime ?? undefined,
  end_datetime: s.end_datetime ?? undefined,
});

type ModalState = {
  type: RegionChannelSettingType;
  regionCode: string | null;
  editingId: string | null;
  // Preserved from the edited row so saving edits doesn't silently flip a disabled rule back on;
  // defaults to true for a brand-new rule.
  enabled: boolean;
  form: Record<string, string>;
  // Kept out of `form` (which is the type-specific channel fields) because these are shared by
  // every setting type and are dates rather than free text.
  startDatetime: Dayjs | null;
  endDatetime: Dayjs | null;
};

type ConfirmState = {
  title: string;
  message: React.ReactNode;
  confirmLabel: string;
  confirmColor?: 'error' | 'primary';
  onConfirm: () => void;
} | null;

function RegionRulePage() {
  const { enqueueSnackbar } = useSnackbar();
  const { data, isLoading, isError } = useListRegionChannelSettings({
    query: { status: 'active', pageSize: 500 },
  });
  const createSetting = useCreateRegionChannelSetting();
  const updateSetting = useUpdateRegionChannelSetting();
  const deleteSetting = useDeleteRegionChannelSetting();

  const settings = useMemo(() => data?.result?.regionChannelSettingList ?? [], [data]);
  const globalSettings = useMemo(() => settings.filter((s) => !s.region_code), [settings]);
  const globalChannelKeys = useMemo(() => new Set(globalSettings.map(channelKey)), [globalSettings]);

  const settingsByRegion = useMemo(() => {
    const map = new Map<string, Array<RegionChannelSetting>>();
    for (const s of settings) {
      if (!s.region_code) {
        continue;
      }
      const list = map.get(s.region_code) ?? [];
      list.push(s);
      map.set(s.region_code, list);
    }
    return map;
  }, [settings]);

  const [addedRegions, setAddedRegions] = useState<Array<string>>([]);
  const visibleRegionCodes = useMemo(() => {
    const set = new Set<string>([...settingsByRegion.keys(), ...addedRegions]);
    return [...set].sort();
  }, [settingsByRegion, addedRegions]);

  const [modal, setModal] = useState<ModalState | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState>(null);
  const busy = createSetting.isPending || updateSetting.isPending || deleteSetting.isPending;

  const openCreate = (type: RegionChannelSettingType, regionCode: string | null) => {
    const cfg = configFor(type);
    if (!cfg) {
      return;
    }
    setModal({
      type,
      regionCode,
      editingId: null,
      enabled: true,
      form: cfg.initialForm(),
      startDatetime: null,
      endDatetime: null,
    });
  };
  const openEdit = (setting: RegionChannelSetting) => {
    const cfg = configFor(setting.setting_type);
    if (!cfg) {
      return;
    }
    setModal({
      type: setting.setting_type,
      regionCode: setting.region_code ?? null,
      editingId: setting.region_channel_setting_id,
      enabled: setting.enabled,
      form: cfg.initialForm(setting),
      startDatetime: isoToPicker(setting.start_datetime),
      endDatetime: isoToPicker(setting.end_datetime),
    });
  };
  const closeModal = () => setModal(null);

  // Deep link from order details: /marketing/region-rule?id=<setting id> opens that rule for
  // editing. The param is consumed so refreshing or closing the modal doesn't reopen it.
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedSettingId = searchParams.get('id');

  // The tab lives in the URL so it can be linked, bookmarked and opened in a new browser tab.
  // Everything else in the query string is carried across, which keeps a resolved customer intact
  // when you flip to Settings to check a rule and come back.
  const tab = searchParams.get('tab') === 'resolve' ? 'resolve' : 'settings';
  const tabHref = (next: 'settings' | 'resolve') => {
    const params = new URLSearchParams(searchParams);
    if (next === 'settings') {
      params.delete('tab');
    } else {
      params.set('tab', next);
    }
    params.delete('id');
    const qs = params.toString();
    return { pathname: '/marketing/region-rule', search: qs ? `?${qs}` : '' };
  };
  useEffect(() => {
    if (!linkedSettingId || isLoading) {
      return;
    }
    const linked = settings.find((s) => s.region_channel_setting_id === linkedSettingId);
    if (linked) {
      openEdit(linked);
    } else {
      enqueueSnackbar('Setting not found — it may have been deleted, or predate the Region rules migration', {
        variant: 'warning',
      });
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('id');
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedSettingId, isLoading]);

  const handleSave = () => {
    if (!modal) {
      return;
    }
    const cfg = configFor(modal.type);
    if (!cfg) {
      return;
    }
    // A picker holding an incomplete date reports an invalid value rather than null. Refusing to
    // save is the point: silently dropping it would store "no bound" while the operator believes
    // they set one.
    const invalidBound = [
      ['Start', modal.startDatetime],
      ['End', modal.endDatetime],
    ].find(([, value]) => value !== null && !(value as Dayjs).isValid());
    if (invalidBound) {
      enqueueSnackbar(`${invalidBound[0]} datetime is incomplete — set both a date and a time, or clear it`, {
        variant: 'error',
      });
      return;
    }

    const start_datetime = pickerToIso(modal.startDatetime);
    const end_datetime = pickerToIso(modal.endDatetime);
    if (start_datetime && end_datetime && new Date(end_datetime) < new Date(start_datetime)) {
      enqueueSnackbar('End datetime must be after start datetime', { variant: 'error' });
      return;
    }
    const payload = {
      ...cfg.toPayload(modal.form, modal.regionCode),
      enabled: modal.enabled,
      start_datetime,
      end_datetime,
    };
    const onSuccess = () => {
      enqueueSnackbar('Setting saved', { variant: 'success' });
      closeModal();
    };
    const onError = () => enqueueSnackbar('Failed to save setting', { variant: 'error' });
    if (modal.editingId) {
      updateSetting.mutate({ id: modal.editingId, payload }, { onSuccess, onError });
    } else {
      createSetting.mutate(payload, { onSuccess, onError });
    }
  };

  const handleToggle = (setting: RegionChannelSetting) => {
    updateSetting.mutate(
      { id: setting.region_channel_setting_id, payload: { ...settingToPayload(setting), enabled: !setting.enabled } },
      { onError: () => enqueueSnackbar('Failed to update setting', { variant: 'error' }) },
    );
  };

  const handleDelete = (id: string) => {
    deleteSetting.mutate(id, {
      onSuccess: () => enqueueSnackbar('Setting removed', { variant: 'success' }),
      onError: () => enqueueSnackbar('Failed to remove setting', { variant: 'error' }),
    });
  };

  // Removes a whole region: expires every setting scoped to it, then drops the tile. Waits for all
  // deletions to settle before reporting, so we don't show success while some are still in flight
  // (or have failed and will reappear on the next refetch).
  const handleDeleteRegion = async (code: string) => {
    const rows = settingsByRegion.get(code) ?? [];
    const results = await Promise.allSettled(rows.map((r) => deleteSetting.mutateAsync(r.region_channel_setting_id)));
    setAddedRegions((prev) => prev.filter((c) => c !== code));
    const failures = results.filter((r) => r.status === 'rejected').length;
    if (failures > 0) {
      enqueueSnackbar(`Failed to remove ${failures} setting(s) in ${regionName(code)}`, { variant: 'error' });
    } else {
      enqueueSnackbar(`Removed ${regionName(code)}`, { variant: 'success' });
    }
  };

  const renderManagedTable = (cfg: SettingTypeConfig, rows: Array<RegionChannelSetting>) => (
    <Table size="small" sx={FIXED_TABLE}>
      <TableHead>
        <TableRow>
          {cfg.columns.map((c) => (
            <TableCell key={c.key} sx={{ ...TYPE_SCALE.columnHead, width: utmColWidth(cfg.columns.length) }}>
              {c.label}
            </TableCell>
          ))}
          <TableCell sx={{ ...TYPE_SCALE.columnHead, width: '21%' }}>Status</TableCell>
          <TableCell align="right" sx={{ ...TYPE_SCALE.columnHead, width: '11%' }}>
            Actions
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((s) => (
          <TableRow key={s.region_channel_setting_id} hover sx={{ '& td': { borderColor: 'divider' } }}>
            {cfg.columns.map((c) => (
              <TableCell key={c.key} sx={{ opacity: s.enabled ? 1 : 0.55, overflowWrap: 'anywhere' }}>
                <PatternValue value={cfg.getCell(s, c.key)} />
              </TableCell>
            ))}
            <TableCell>
              <Chip size="small" color={s.enabled ? 'success' : 'default'} label={s.enabled ? 'Enabled' : 'Disabled'} />
              <WindowNote setting={s} />
            </TableCell>
            <TableCell align="right">
              <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                <Tooltip title={s.enabled ? 'Disable' : 'Enable'}>
                  <Switch size="small" checked={s.enabled} disabled={busy} onChange={() => handleToggle(s)} />
                </Tooltip>
                <IconButton size="small" aria-label="edit" disabled={busy} onClick={() => openEdit(s)}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label="expire"
                  disabled={busy}
                  onClick={() =>
                    setConfirm({
                      title: 'Delete setting',
                      message: (
                        <Box>
                          <Typography variant="body2">Are you sure you want to delete this setting?</Typography>
                          <Typography variant="subtitle2" sx={{ mt: 1.5 }}>
                            {cfg.label}
                          </Typography>
                          <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                            {cfg.columns.map((c) => (
                              <Typography component="li" variant="body2" key={c.key}>
                                {c.label.toLowerCase()}: {cfg.getCell(s, c.key) || 'any'}
                              </Typography>
                            ))}
                          </Box>
                        </Box>
                      ),
                      confirmLabel: 'Delete',
                      confirmColor: 'error',
                      onConfirm: () => handleDelete(s.region_channel_setting_id),
                    })
                  }
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
            </TableCell>
          </TableRow>
        ))}
        {rows.length === 0 && (
          <TableRow>
            <TableCell colSpan={cfg.columns.length + 2}>
              <Typography variant="body2" color="text.secondary">
                No settings configured.
              </Typography>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );

  const renderInheritedTable = (
    cfg: SettingTypeConfig,
    regionCode: string,
    regionRows: Array<RegionChannelSetting>,
  ) => {
    const globalsOfType = globalSettings.filter((s) => s.setting_type === cfg.type);
    if (globalsOfType.length === 0) {
      return null;
    }
    return (
      <Table size="small" sx={FIXED_TABLE}>
        <TableHead>
          <TableRow>
            {cfg.columns.map((c) => (
              <TableCell key={c.key} sx={{ ...TYPE_SCALE.columnHead, width: utmColWidth(cfg.columns.length) }}>
                {c.label}
              </TableCell>
            ))}
            <TableCell align="right" sx={{ ...TYPE_SCALE.columnHead, width: '32%' }}>
              This region
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {globalsOfType.map((g) => {
            const shadow = regionRows.find((r) => channelKey(r) === channelKey(g) && !r.enabled);
            const mode = shadow ? 'disable' : 'inherit';
            const onModeChange = (_e: React.MouseEvent<HTMLElement>, next: string | null) => {
              if (!next || next === mode) {
                return;
              }
              if (next === 'disable') {
                createSetting.mutate(
                  {
                    setting_type: g.setting_type,
                    region_code: regionCode,
                    source: g.source ?? undefined,
                    medium: g.medium ?? undefined,
                    campaign: g.campaign ?? undefined,
                    term: g.term ?? undefined,
                    content: g.content ?? undefined,
                    enabled: false,
                  },
                  { onError: () => enqueueSnackbar('Failed to update region', { variant: 'error' }) },
                );
              } else if (shadow) {
                handleDelete(shadow.region_channel_setting_id);
              }
            };
            return (
              <TableRow key={g.region_channel_setting_id} hover sx={{ '& td': { borderColor: 'divider' } }}>
                {cfg.columns.map((c) => (
                  <TableCell key={c.key} sx={{ opacity: mode === 'disable' ? 0.55 : 1, overflowWrap: 'anywhere' }}>
                    <PatternValue value={cfg.getCell(g, c.key)} />
                  </TableCell>
                ))}
                <TableCell align="right">
                  <ToggleButtonGroup size="small" exclusive value={mode} onChange={onModeChange} disabled={busy}>
                    <ToggleButton value="inherit">Inherit</ToggleButton>
                    <ToggleButton value="disable">Disable</ToggleButton>
                  </ToggleButtonGroup>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>Marketing / Region / Channel Control</title>
      </Helmet>

      <Container maxWidth="xl" sx={{ mt: 2 }}>
        <PageHeader title="Region Rules" />

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tab}>
            <Tab label="Settings" value="settings" component={RouterLink} to={tabHref('settings')} />
            <Tab label="Resolve a customer" value="resolve" component={RouterLink} to={tabHref('resolve')} />
          </Tabs>
        </Box>

        {tab === 'resolve' && (
          <RegionRuleResolveTab settings={settings} labelForType={(type) => configFor(type)?.label ?? type} />
        )}

        <Stack spacing={3} sx={{ p: 2, display: tab === 'settings' ? undefined : 'none' }}>
          {isError && <Alert severity="error">Failed to load settings.</Alert>}
          {/* Global settings */}
          <Typography component="span" sx={TYPE_SCALE.section}>
            Global
          </Typography>
          <Paper
            variant="outlined"
            sx={{ p: 2, borderLeft: 4, borderLeftColor: SCOPE_RAIL.global, mt: '4px !important' }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Applies to every region unless a region below overrides it.
            </Typography>
            <Stack spacing={2.5}>
              {SETTING_TYPES.map((cfg) => {
                const rows = globalSettings.filter((s) => s.setting_type === cfg.type);
                if (rows.length === 0) {
                  return null;
                }
                return (
                  <Box key={cfg.type}>
                    <Typography sx={{ ...TYPE_SCALE.typeLabel, mb: 0.75 }}>{cfg.label}</Typography>
                    {renderManagedTable(cfg, rows)}
                  </Box>
                );
              })}
            </Stack>
            <Box sx={{ mt: 2 }}>
              <AddSettingDropdown onAdd={(type) => openCreate(type, null)} disabled={busy} />
            </Box>
          </Paper>

          <Typography component="span" sx={{ ...TYPE_SCALE.section, pt: 1 }}>
            Regions · {visibleRegionCodes.length}
          </Typography>

          <AddRegionSelector
            existingRegionCodes={visibleRegionCodes}
            onAddRegion={(code) =>
              setConfirm({
                title: 'Add region',
                message: (
                  <Typography variant="body2">
                    Add {regionName(code)} ({code}) to region rules?
                  </Typography>
                ),
                confirmLabel: 'Add',
                confirmColor: 'primary',
                onConfirm: () => setAddedRegions((prev) => [...prev, code]),
              })
            }
          />

          {visibleRegionCodes.map((code) => {
            const regionRows = settingsByRegion.get(code) ?? [];
            // Counts what is actually in force here. A row that only exists to disable an inherited
            // global rule is not a rule this region applies, so it does not count.
            const enabledCount = regionRows.filter((s) => s.enabled).length;
            return (
              <Paper key={code} variant="outlined" sx={{ p: 2, borderLeft: 4, borderLeftColor: SCOPE_RAIL.region }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    mb: 2,
                    pb: 1.25,
                    borderBottom: 1,
                    borderColor: 'divider',
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="baseline">
                    <Typography sx={TYPE_SCALE.regionName}>{regionName(code)}</Typography>
                    <Typography
                      component="span"
                      sx={{ fontFamily: MONO, fontSize: 12, fontWeight: 700, color: SCOPE_RAIL.region }}
                    >
                      {code}
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      {enabledCount} rule{enabledCount === 1 ? '' : 's'} enabled
                    </Typography>
                  </Stack>
                  <Tooltip title="Remove region">
                    <IconButton
                      aria-label="remove region"
                      disabled={busy}
                      onClick={() =>
                        setConfirm({
                          title: 'Delete region',
                          message: (
                            <Typography variant="body2">
                              Are you sure you want to delete {regionName(code)} ({code})? This removes{' '}
                              {regionRows.length} region setting(s).
                            </Typography>
                          ),
                          confirmLabel: 'Delete',
                          confirmColor: 'error',
                          onConfirm: () => void handleDeleteRegion(code),
                        })
                      }
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>

                <Typography component="span" sx={{ ...TYPE_SCALE.subLabel, mb: 1.25 }}>
                  Inherited from global
                </Typography>
                <Stack spacing={2.5} sx={{ mb: 3 }}>
                  {SETTING_TYPES.map((cfg) => {
                    const table = renderInheritedTable(cfg, code, regionRows);
                    if (!table) {
                      return null;
                    }
                    return (
                      // The negative margin cancels the padding, so the tint bleeds wider than the
                      // content while the table keeps the same left origin as the region-specific
                      // tables below — otherwise the two sections' columns sit 12px apart.
                      <Box
                        key={cfg.type}
                        sx={{ bgcolor: 'action.hover', borderRadius: 1, px: 1.5, mx: -1.5, py: 1.25 }}
                      >
                        <Typography sx={{ ...TYPE_SCALE.typeLabel, mb: 0.75 }}>{cfg.label}</Typography>
                        {table}
                      </Box>
                    );
                  })}
                </Stack>

                <Typography component="span" sx={{ ...TYPE_SCALE.subLabel, mb: 1.25 }}>
                  Region-specific settings
                </Typography>
                <Stack spacing={2.5}>
                  {SETTING_TYPES.map((cfg) => {
                    // Region-specific rows exclude "disable" shadows of a global (those show in the
                    // Inherited section as the Disable state).
                    const rows = regionRows.filter(
                      (s) => s.setting_type === cfg.type && !(!s.enabled && globalChannelKeys.has(channelKey(s))),
                    );
                    if (rows.length === 0) {
                      return null;
                    }
                    return (
                      <Box key={cfg.type}>
                        <Typography sx={{ ...TYPE_SCALE.typeLabel, mb: 0.75 }}>{cfg.label}</Typography>
                        {renderManagedTable(cfg, rows)}
                      </Box>
                    );
                  })}
                </Stack>
                <Box sx={{ mt: 2 }}>
                  <AddSettingDropdown onAdd={(type) => openCreate(type, code)} disabled={busy} />
                </Box>
              </Paper>
            );
          })}

          {/* Create / edit modal */}
          <Dialog open={!!modal} onClose={closeModal} fullWidth maxWidth="sm">
            {modal &&
              (() => {
                const cfg = configFor(modal.type);
                if (!cfg) {
                  return null;
                }
                return (
                  <>
                    <DialogTitle>
                      {modal.editingId ? 'Edit' : 'Add'} — {cfg.label}
                      {modal.regionCode ? ` (${regionName(modal.regionCode)})` : ' (Global)'}
                    </DialogTitle>
                    <DialogContent>
                      <Stack spacing={1} sx={{ mb: 2 }}>
                        {cfg.formChannelHelper && (
                          <Typography variant="body2" color="text.secondary">
                            {cfg.formChannelHelper}
                          </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary">
                          Times are UTC — leave a datetime blank for no bound (both blank = long term).
                        </Typography>
                      </Stack>
                      <Stack spacing={2} sx={{ mt: 1 }}>
                        {cfg.formFields.map((f) => (
                          <TextField
                            key={f.key}
                            label={f.label}
                            helperText={f.helper}
                            value={modal.form[f.key] ?? ''}
                            onChange={(e) =>
                              setModal((m) => (m ? { ...m, form: { ...m.form, [f.key]: e.target.value } } : m))
                            }
                            fullWidth
                          />
                        ))}
                        <DateTimePicker
                          label="Start datetime (UTC)"
                          timezone="UTC"
                          value={modal.startDatetime}
                          onChange={(value) => setModal((m) => (m ? { ...m, startDatetime: value } : m))}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              helperText: 'Blank = no start bound (active from now).',
                            },
                            field: { clearable: true },
                          }}
                        />
                        <DateTimePicker
                          label="End datetime (UTC)"
                          timezone="UTC"
                          value={modal.endDatetime}
                          onChange={(value) => setModal((m) => (m ? { ...m, endDatetime: value } : m))}
                          slotProps={{
                            textField: { fullWidth: true, helperText: 'Blank = no end bound.' },
                            field: { clearable: true },
                          }}
                        />
                      </Stack>
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={closeModal} disabled={busy}>
                        Cancel
                      </Button>
                      <Button variant="contained" onClick={handleSave} disabled={busy}>
                        Save
                      </Button>
                    </DialogActions>
                  </>
                );
              })()}
          </Dialog>

          {/* Confirmation dialog — row delete, region delete, and add region */}
          <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
            <DialogTitle>{confirm?.title}</DialogTitle>
            <DialogContent>{confirm?.message}</DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirm(null)}>Cancel</Button>
              <Button
                variant="contained"
                color={confirm?.confirmColor ?? 'primary'}
                onClick={() => {
                  confirm?.onConfirm();
                  setConfirm(null);
                }}
              >
                {confirm?.confirmLabel ?? 'Confirm'}
              </Button>
            </DialogActions>
          </Dialog>
        </Stack>
      </Container>
    </>
  );
}

function AddSettingDropdown({
  onAdd,
  disabled,
}: {
  onAdd: (type: RegionChannelSettingType) => void;
  disabled?: boolean;
}) {
  const [value, setValue] = useState('');
  return (
    <FormControl size="small" sx={{ minWidth: 320 }}>
      <InputLabel id="add-setting">Add new setting</InputLabel>
      <Select
        labelId="add-setting"
        label="Add new setting"
        value={value}
        disabled={disabled}
        onChange={(e: SelectChangeEvent) => {
          const type = e.target.value as RegionChannelSettingType;
          if (type) {
            onAdd(type);
          }
          setValue('');
        }}
      >
        {SETTING_TYPES.map((cfg) => (
          <MenuItem key={cfg.type} value={cfg.type}>
            {cfg.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function AddRegionSelector({
  existingRegionCodes,
  onAddRegion,
}: {
  existingRegionCodes: Array<string>;
  onAddRegion: (code: string) => void;
}) {
  const [value, setValue] = useState('');
  const available = allRegions.filter((r) => !existingRegionCodes.includes(r.code));
  return (
    <FormControl size="small" sx={{ minWidth: 260 }}>
      <InputLabel id="add-region">Add region</InputLabel>
      <Select
        labelId="add-region"
        label="Add region"
        value={value}
        onChange={(e: SelectChangeEvent) => {
          if (e.target.value) {
            onAddRegion(e.target.value);
          }
          setValue('');
        }}
      >
        {available.map((r) => (
          <MenuItem key={r.code} value={r.code}>
            {r.name} ({r.code})
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export default RegionRulePage;
